import React from 'react';
import { Button, Text, type Colors } from '@user-portal/wikr-components';

import OverlineButton from 'components/OverlineButton';
import OutlinedButton from 'components/OutlinedButton';
import Modal from 'components/Modal';
import ContactUsLink from 'components/ContactUsLink';

import * as S from './styled';

import hands from 'assets/img/webp/well-done-hands.webp';
import decline from 'assets/img/webp/decline.webp';
import errorAttention from 'assets/img/webp/attention.webp';
import warning from 'assets/img/svg/warning.svg';
import thumbUp from 'assets/images/thumbUp.webp';
import success from 'assets/images/success.png';
import smile from 'assets/images/smile.png';

const IMAGE_NAME_MAP = {
    smile,
    warning,
    decline,
    success,
    thumbUp,
    errorAttention,
    'rising-hands': hands,
};

export interface UniversalModalProps {
    image?: keyof typeof IMAGE_NAME_MAP;
    onClose?: () => void;
    trackLoad?: () => void;
    onPrimaryClick?: () => void;
    onSecondaryClick?: () => void;
    onTertiaryClick?: () => void;
    onContactUsClick?: () => void;
    withCloseButton?: boolean;
    withContactUsLink?: boolean;
    captions: {
        title: string;
        subtitle?: string;
        extraText?: {
            text?: string;
            color?: Colors;
        };
        primaryButton?: string;
        secondaryButton?: string;
        tertiaryButton?: string;
    };
    dataLocators?: {
        modal?: string;
        container?: string;
        primaryButton?: string;
        secondaryButton?: string;
        tertiaryButton?: string;
        closeButton?: string;
    };
}

const UniversalModal = ({
    onPrimaryClick,
    onSecondaryClick,
    onTertiaryClick,
    onClose,
    trackLoad,
    captions,
    dataLocators,
    withCloseButton = false,
    image,
    withContactUsLink,
    onContactUsClick,
}: UniversalModalProps) => {
    return (
        <Modal
            isOpen
            onClose={onClose}
            closeOnOverlayClick
            withCloseButton={withCloseButton}
            trackLoad={trackLoad}
            paddingY={24}
            dataLocatorContainer={dataLocators?.container}
        >
            <div data-locator={dataLocators?.modal} style={{ paddingTop: '40px' }}>
                {image && <S.Img src={IMAGE_NAME_MAP[image]} alt="modal image" />}

                <Text type="h5" center mb={12}>
                    {captions.title}
                </Text>

                {captions.subtitle && (
                    <Text center mb={12}>
                        {captions.subtitle}
                    </Text>
                )}

                {captions.extraText && (
                    <Text center color={captions.extraText.color} mb={12}>
                        {captions.extraText.text}
                    </Text>
                )}

                <S.ButtonsBlock>
                    {onPrimaryClick && (
                        <Button dataLocator={dataLocators?.primaryButton} onClick={onPrimaryClick} mb={20}>
                            {captions.primaryButton || 'Primary'}
                        </Button>
                    )}

                    {onSecondaryClick && (
                        <OutlinedButton dataLocator={dataLocators?.secondaryButton} onClick={onSecondaryClick} mb={20}>
                            {captions.secondaryButton || 'Secondary'}
                        </OutlinedButton>
                    )}

                    {onTertiaryClick && (
                        <OverlineButton dataLocator={dataLocators?.tertiaryButton} onClick={onTertiaryClick} mb={4}>
                            {captions.tertiaryButton || 'Tertiary'}
                        </OverlineButton>
                    )}
                    {withContactUsLink && (
                        <ContactUsLink onClick={onContactUsClick} text={captions.tertiaryButton} boldText />
                    )}
                </S.ButtonsBlock>
            </div>
        </Modal>
    );
};

export default UniversalModal;
